/**
 * errorSanitizer.js - Sanitize error messages before displaying to users
 * Prevents leaking API keys, tokens, and other sensitive data in the UI
 */

const SENSITIVE_PATTERNS = [
  /api_key[=:]\s*\S+/gi,
  /[?&]key=\S+/gi,
  /AIza[0-9A-Za-z_-]{30,}/g,
  /Bearer\s+\S+/gi,
  /secret[=:]\s*\S+/gi,
  /token[=:]\s*\S+/gi,
  /password[=:]\s*\S+/gi,
  /credential[=:]\s*\S+/gi,
  /AKIA[0-9A-Z]{16}/g, // AWS Access Key
  /bot[0-9]+:[A-Za-z0-9_-]+/gi, // Telegram bot token
];

const KNOWN_ERROR_MAPPINGS = [
  {
    pattern: /CONSUMER_SUSPENDED|has been suspended/i,
    message: "Dịch vụ AI tạm thời bị gián đoạn. Vui lòng liên hệ admin.",
  },
  {
    pattern: /API_KEY_INVALID|API key not valid/i,
    message: "Cấu hình AI không hợp lệ. Vui lòng liên hệ admin.",
  },
  {
    pattern: /QUOTA_EXCEEDED|quota/i,
    message: "Đã vượt giới hạn sử dụng AI. Vui lòng thử lại sau.",
  },
  {
    pattern: /PERMISSION_DENIED|Permission denied/i,
    message: "Không có quyền truy cập dịch vụ AI. Vui lòng liên hệ admin.",
  },
  {
    pattern: /generativelanguage\.googleapis\.com/i,
    message: "Lỗi kết nối dịch vụ AI. Vui lòng thử lại sau.",
  },
  {
    pattern: /ECONNREFUSED|ETIMEDOUT|ENOTFOUND|NetworkError/i,
    message: "Lỗi kết nối mạng. Vui lòng kiểm tra kết nối internet.",
  },
];

/**
 * Sanitize an error message to remove sensitive information
 * @param {string|Error} error - Raw error message or Error object
 * @param {string} [fallback] - Optional fallback message
 * @returns {string} Safe error message for UI display
 */
export function sanitizeError(error, fallback = "Có lỗi xảy ra. Vui lòng thử lại.") {
  if (!error) return fallback;

  const message = typeof error === "string" ? error : error.message || String(error);

  // Check known error patterns first — return friendly message
  for (const mapping of KNOWN_ERROR_MAPPINGS) {
    if (mapping.pattern.test(message)) {
      return mapping.message;
    }
  }

  // Strip sensitive data from unknown errors
  let safe = message;
  for (const pattern of SENSITIVE_PATTERNS) {
    safe = safe.replace(pattern, "[REDACTED]");
  }

  // If the message is too long (likely a raw stack trace), truncate
  if (safe.length > 200) {
    safe = safe.substring(0, 200) + "...";
  }

  return safe;
}

/**
 * Sanitize a server error response
 * Use this when displaying error from API responses
 * @param {Object} data - Response data from server
 * @param {string} [fallback] - Optional fallback message
 * @returns {string} Safe error message
 */
export function sanitizeServerError(data, fallback = "Có lỗi xảy ra từ server.") {
  if (!data) return fallback;
  const raw = data.error || data.message || data.detail || "";
  return sanitizeError(raw, fallback);
}

export default sanitizeError;
